import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { IAddtionalRequest } from "../../Interfaces/IAddtionalRequest"
import { IProductRequest } from "../../Interfaces/IProductRequest"
import { ISelectedSelectionRequest, ISelectionRequest } from "../../Interfaces/ISelectionRequest"
import { getAddOnsTotal, getSelectionsTotal } from "../Helpers/CalculateCartSummary"
import { ResolveSelectionOptionPriceLabel } from "../Helpers/ResolveSelectionOptionPriceLabel"
import { companyMemory } from "../../Objects/Memory/Memory"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { IPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"
import { EProductOrderModeType } from "../../Enum/EProductOrderModeType"
import { toReservationModeLabel } from "../../Extensions/ReservationExtensions"
import { Bolt, CalendarClock, ChevronLeft, ChevronRight, X } from "lucide-react"
import {
  normalizeReservationDateForInput,
  normalizeReservationTimeForInput,
} from "../../Extensions/ReservationExtensions"
import { PublicMenuImageThumbnail } from "../Components/PublicMenuImageThumbnail"
import { getProductImages } from "../Helpers/ProductImages"
import { PublicMenuImagePreviewPupop } from "./PublicMenuImagePreviewPupop"

interface AddMealRequestPupopProps {
  product: IProductRequest
  onClose: () => void
  onConfirm: (
    productRequest: IProductRequest,
    selectedAddOns: IAddtionalRequest[],
    selectedSelections: ISelectedSelectionRequest[],
    quantity: number,
    notes: string,
    reservationInfo: {
      isReservation: boolean
      reservationDate: string
      reservationTime: string
    },
  ) => void
  initialSelectedAddOns?: IAddtionalRequest[]
  initialQuantity?: number
  initialNotes?: string
  confirmLabel?: string
  disableConfirm?: boolean
  hideConfirm?: boolean
  theme?: IPublicMenuTheme | null
  isRodizioIncluded?: boolean
  isRodizioAdditionalsIncluded?: boolean
  initialSelectedSelections?: ISelectedSelectionRequest[]
  initialReservationInfo?: {
    isReservation: boolean
    reservationDate?: string
    reservationTime?: string
  }
}

const resolveSelectionValues = (
  selection: ISelectionRequest,
  selectedItems: ISelectedSelectionRequest[],
): ISelectedSelectionRequest[] => {
  let freeRemaining = Math.max(0, selection.freeOptionsLimit ?? 0)

  return selectedItems.map((item) => {
    if (item.selectionId !== selection.id) {
      return item
    }

    const baseValue = item.baseValue ?? item.value ?? 0

    const isFree = item.isFree ?? baseValue <= 0

    const value = isFree || freeRemaining > 0 ? 0 : baseValue

    if (!isFree && freeRemaining > 0) {
      freeRemaining -= 1
    }

    return { ...item, baseValue, isFree, value }
  })
}

const buildSelectionStatusMessages = (
  selection: ISelectionRequest,
  selectedItems: ISelectedSelectionRequest[],
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  const selectedCount = selectedItems.filter((item) => item.selectionId === selection.id).length

  const requiredOptionsCount = Math.max(1, selection.requiredOptionsCount ?? 1)

  const freeOptionsLimit = Math.max(0, selection.freeOptionsLimit ?? 0)

  const freeUsed = selectedItems.filter(
    (item) => item.selectionId === selection.id && !item.isFree && item.value <= 0,
  ).length

  const freeRemaining = Math.max(0, freeOptionsLimit - freeUsed)

  const maximumLimitCandidates = [
    selection.hasMaximumSelectionsLimit ? Math.max(1, selection.maximumSelectionsLimit ?? 1) : undefined,
  ].filter((limit): limit is number => typeof limit === "number")

  const effectiveMaximumSelectionsLimit =
    maximumLimitCandidates.length > 0 ? Math.min(...maximumLimitCandidates) : undefined

  const maximumRemaining =
    effectiveMaximumSelectionsLimit === undefined ? 0 : Math.max(0, effectiveMaximumSelectionsLimit - selectedCount)

  const messages = [
    {
      text: selection.isRequired
        ? t("publicMenu:add_meal.selected_required_progress", {
            selected: Math.min(selectedCount, requiredOptionsCount),
            total: requiredOptionsCount,
          })
        : t("publicMenu:add_meal.selected_optional_progress", { count: selectedCount }),
      className:
        selection.isRequired && selectedCount >= requiredOptionsCount
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : selection.isRequired
            ? "border-red-200 bg-red-50 text-red-700"
            : "border-sky-200 bg-sky-50 text-sky-700",
    },
  ]

  if (freeOptionsLimit > 0) {
    messages.push({
      text: t("publicMenu:add_meal.free_choices_remaining", { count: freeRemaining }),
      className:
        freeRemaining > 0 ? "border-lime-200 bg-lime-50 text-lime-700" : "border-amber-200 bg-amber-50 text-amber-700",
    })
  }

  if (effectiveMaximumSelectionsLimit !== undefined) {
    messages.push(
      maximumRemaining > 0
        ? {
            text: t("publicMenu:add_meal.maximum_choices_remaining", { count: maximumRemaining }),
            className: "border-violet-200 bg-violet-50 text-violet-700",
          }
        : {
            text: t("publicMenu:add_meal.maximum_choices_reached"),
            className: "border-rose-200 bg-rose-50 text-rose-700",
          },
    )
  }

  return messages
}

export const AddMealRequestPupop: React.FC<AddMealRequestPupopProps> = ({
  product,
  onClose,
  onConfirm,
  initialSelectedAddOns,
  initialQuantity,
  initialNotes,
  confirmLabel,
  disableConfirm = false,
  hideConfirm = false,
  theme,
  isRodizioIncluded = false,
  isRodizioAdditionalsIncluded = false,
  initialSelectedSelections,
  initialReservationInfo,
}) => {
  const { t } = useTranslation()

  const [selectedAddOns, setSelectedAddOns] = useState<IAddtionalRequest[]>(() =>
    (initialSelectedAddOns ?? []).map((addOn) => ({ ...addOn })),
  )

  const [quantity, setQuantity] = useState(Math.max(1, initialQuantity ?? 1))

  const [notes, setNotes] = useState(initialNotes ?? "")

  const [expandedImage, setExpandedImage] = useState<{ image: string; alt: string } | null>(null)

  const [expandedProductImageIndex, setExpandedProductImageIndex] = useState<number | null>(null)

  const productImages = useMemo(() => getProductImages(product), [product])

  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useEffect(() => {
    if (productImages.length <= 1) {
      return
    }

    const interval = window.setInterval(() => {
      setCurrentImageIndex((index) => (index + 1) % productImages.length)
    }, 2000)

    return () => window.clearInterval(interval)
  }, [productImages.length])

  const [selectedSelections, setSelectedSelections] = useState<ISelectedSelectionRequest[]>(
    initialSelectedSelections ?? [],
  )

  const supportsReservation =
    product.productOrderMode === EProductOrderModeType.ReservationOnly ||
    product.productOrderMode === EProductOrderModeType.ImmediateAndReservation

  const supportsImmediate =
    product.productOrderMode === undefined ||
    product.productOrderMode === EProductOrderModeType.ImmediateOnly ||
    product.productOrderMode === EProductOrderModeType.ImmediateAndReservation

  const [selectedFlow, setSelectedFlow] = useState<"immediate" | "reservation">(
    initialReservationInfo?.isReservation
      ? "reservation"
      : product.productOrderMode === EProductOrderModeType.ReservationOnly
        ? "reservation"
        : "immediate",
  )

  const reservationDates = Array.from({ length: 21 })
    .map((_, index) => {
      const date = new Date()
      date.setDate(date.getDate() + index)
      return date
    })
    .filter((date) => (product.reservationAllowedDays ?? []).includes(date.getDay()))

  const [reservationDate, setReservationDate] = useState<string>(
    normalizeReservationDateForInput(initialReservationInfo?.reservationDate) ||
      (reservationDates.length > 0 ? reservationDates[0].toInputDate() : ""),
  )

  const [reservationTime, setReservationTime] = useState<string>(
    normalizeReservationTimeForInput(initialReservationInfo?.reservationTime) ||
      normalizeReservationTimeForInput((product.reservationAllowedTimes ?? [])[0]) ||
      "",
  )

  const isReadOnly = disableConfirm

  const askWaiterButtonColor = theme?.buttonColor || "#DC2626"

  const askWaiterButtonTextColor = theme?.buttonTextColor || "#FFFFFF"

  const isLegacyTheme = !theme || isLegacyPublicMenuTheme(theme)

  const increaseAddOn = (addOn: IAddtionalRequest) => {
    setSelectedAddOns((prev) => {
      const existing = prev.find((a) => a.id === addOn.id)

      if (existing) {
        return prev.map((a) => (a.id === addOn.id ? { ...a, quantity: a.quantity + 1 } : a))
      }

      return [...prev, { ...addOn, quantity: 1 }]
    })
  }

  const decreaseAddOn = (addOnId: number) => {
    setSelectedAddOns((prev) =>
      prev.map((a) => (a.id === addOnId ? { ...a, quantity: a.quantity - 1 } : a)).filter((a) => a.quantity > 0),
    )
  }

  const hasPromotion = !isRodizioIncluded && product.oldPrice && product.oldPrice > product.price

  const discountPercent = hasPromotion ? Math.round(((product.oldPrice! - product.price) / product.oldPrice!) * 100) : 0

  const freeLimit = product.freeAddtionalsLimit ?? 0

  const addOnsTotal = isRodizioAdditionalsIncluded ? 0 : getAddOnsTotal(selectedAddOns, freeLimit)

  const selectionsTotal = isRodizioAdditionalsIncluded ? 0 : getSelectionsTotal(selectedSelections)

  const currentTotalPrice = (product.price + addOnsTotal + selectionsTotal) * quantity

  const missingRequiredSelections =
    product.availableSelections?.filter(
      (selection) =>
        selection.isRequired &&
        selectedSelections.filter((item) => item.selectionId === selection.id).length <
          Math.max(1, selection.requiredOptionsCount ?? 1),
    ) ?? []

  const hasMissingRequiredSelections = missingRequiredSelections.length > 0

  const missingRequiredSelectionNames = missingRequiredSelections.map((selection) => selection.name).join(", ")

  const getEffectiveSelectionMaximum = (selection: ISelectionRequest) =>
    selection.hasMaximumSelectionsLimit ? Math.max(1, selection.maximumSelectionsLimit ?? 1) : undefined

  const createSelectionItem = (
    selection: ISelectionRequest,
    option: ISelectionRequest["options"][number],
  ): ISelectedSelectionRequest => {
    const baseValue = option.isFree ? 0 : option.value

    return {
      selectionId: selection.id,
      optionId: option.id,
      selectionName: selection.name,
      optionName: option.name,
      value: baseValue,
      baseValue,
      isFree: option.isFree,
    }
  }

  const increaseSelectionOption = (selection: ISelectionRequest, option: ISelectionRequest["options"][number]) => {
    setSelectedSelections((prev) => {
      const currentSelectionItems = prev.filter((item) => item.selectionId === selection.id)

      const currentOptionCount = currentSelectionItems.filter((item) => item.optionId === option.id).length

      const selectionMaximum = getEffectiveSelectionMaximum(selection)

      const optionMaximum =
        selection.allowMultiple && option.allowMultiple ? Math.max(1, option.maximumSelectionsLimit ?? 1) : 1

      if (selectionMaximum !== undefined && currentSelectionItems.length >= selectionMaximum) {
        return prev
      }

      if (currentOptionCount >= optionMaximum) {
        return prev
      }

      return resolveSelectionValues(selection, [...prev, createSelectionItem(selection, option)])
    })
  }

  const decreaseSelectionOption = (selection: ISelectionRequest, option: ISelectionRequest["options"][number]) => {
    setSelectedSelections((prev) => {
      const removeIndex = prev.findIndex((item) => item.selectionId === selection.id && item.optionId === option.id)

      if (removeIndex < 0) {
        return prev
      }

      const next = prev.filter((_, index) => index !== removeIndex)

      return resolveSelectionValues(selection, next)
    })
  }

  const toggleSelectionOption = (
    selection: ISelectionRequest,
    option: ISelectionRequest["options"][number],
    checked: boolean,
  ) => {
    setSelectedSelections((prev) => {
      const currentSelectionItems = prev.filter((item) => item.selectionId === selection.id)

      const selectionMaximum = getEffectiveSelectionMaximum(selection)

      const newItem = createSelectionItem(selection, option)

      if (selection.allowMultiple) {
        if (checked) {
          const next = prev.filter((item) => !(item.selectionId === selection.id && item.optionId === option.id))

          return resolveSelectionValues(selection, next)
        }

        if (selectionMaximum !== undefined && currentSelectionItems.length >= selectionMaximum) {
          return prev
        }

        return resolveSelectionValues(selection, [...prev, newItem])
      }

      return resolveSelectionValues(selection, [...prev.filter((item) => item.selectionId !== selection.id), newItem])
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
            <div
              className="bg-white ring-2 ring-orange-500 w-[calc(100vw-1rem)] h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] max-h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] sm:w-full sm:h-auto sm:max-h-[90vh] max-w-xl rounded-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-500 flex flex-col border border-white/20"
              style={
                isLegacyTheme || !theme
                  ? undefined
                  : {
                      backgroundColor: theme.surfaceColor,
                      boxShadow: `0 0 0 2px ${theme.borderColor}`,
                      color: theme.textColor,
                    }
              }
            >
              <div className="relative h-40 sm:h-56 shrink-0 overflow-hidden bg-white">
                {productImages.length > 1 ? (
                  <div
                    className="flex h-full transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                  >
                    {productImages.map((image, index) => (
                      <button
                        key={`${image}-${index}`}
                        type="button"
                        onClick={() => setExpandedProductImageIndex(index)}
                        aria-label={t("publicMenu:add_meal_gallery.image_alt", {
                          name: product.name,
                          index: index + 1,
                        })}
                        className="h-full w-full shrink-0 cursor-zoom-in bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500"
                        data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.imageAlt"
                      >
                        <img
                          src={image.toClientRequest()}
                          className="h-full w-full bg-white object-cover transition-opacity duration-500"
                          alt={t("publicMenu:add_meal_gallery.image_alt", { name: product.name, index: index + 1 })}
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setExpandedProductImageIndex(0)}
                    aria-label={product.name}
                    className="h-full w-full cursor-zoom-in bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500"
                    data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.productName"
                  >
                    <img
                      src={(productImages[0] ?? product.image).toClientRequest()}
                      className="h-full w-full bg-white object-cover"
                      alt={product.name}
                    />
                  </button>
                )}

                {productImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentImageIndex((index) => (index - 1 + productImages.length) % productImages.length)
                      }
                      className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/40 bg-black/55 p-2 text-white shadow-lg transition-all hover:scale-110 hover:bg-black/75"
                      aria-label={t("publicMenu:add_meal_gallery.previous_image")}
                      data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.previousImage"
                    >
                      <ChevronLeft />
                    </button>

                    <button
                      type="button"
                      onClick={() => setCurrentImageIndex((index) => (index + 1) % productImages.length)}
                      className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/40 bg-black/55 p-2 text-white shadow-lg transition-all hover:scale-110 hover:bg-black/75"
                      aria-label={t("publicMenu:add_meal_gallery.next_image")}
                      data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.nextImage"
                    >
                      <ChevronRight />
                    </button>

                    <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/35 px-2 py-1.5 backdrop-blur-sm">
                      {productImages.map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setCurrentImageIndex(index)}
                          className={`h-2.5 rounded-full transition-all duration-300 ${index === currentImageIndex ? "w-6 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"}`}
                          aria-label={t("publicMenu:add_meal_gallery.select_image", { index: index + 1 })}
                          data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.selectImage"
                        />
                      ))}
                    </div>
                  </>
                )}

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white via-transparent to-black/30" />

                {hasPromotion && (
                  <div className="absolute top-6 left-6 bg-red-600 text-white px-4 py-1.5 rounded-2xl shadow-xl shadow-red-200 border border-red-500 transform -rotate-2 animate-in slide-in-from-left duration-500">
                    <span className="text-[11px] font-black tracking-widest uppercase">{discountPercent}% OFF</span>
                  </div>
                )}

                <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
                  <button
                    type="button"
                    onClick={onClose}
                    title={t("publicMenu:cart.close")}
                    aria-label={t("publicMenu:cart.close")}
                    className="absolute top-4 right-4 sm:top-6 sm:right-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border-2 border-red-200 bg-red-600 px-3 text-white shadow-xl shadow-red-900/25 ring-2 ring-white/80 transition-all hover:bg-red-700 hover:ring-red-100 active:scale-95"
                    data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.close"
                  >
                    <X size={20} strokeWidth={3} />

                    <span className="hidden text-xs font-black uppercase tracking-[0.12em] sm:inline">
                      {t("publicMenu:cart.close")}
                    </span>
                  </button>
                </Animate>
              </div>

              <div
                className="p-4 sm:p-8 md:p-10 overflow-y-auto no-scrollbar space-y-6 sm:space-y-10 flex-1"
                style={isLegacyTheme || !theme ? undefined : { backgroundColor: theme.surfaceColor }}
              >
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                  <div className="flex justify-between items-start gap-3 sm:gap-6">
                    <div className="flex-1 space-y-2">
                      <h2
                        className="text-xl sm:text-3xl font-black text-gray-900 tracking-tighter uppercase leading-none"
                        style={isLegacyTheme || !theme ? undefined : { color: theme.textColor }}
                      >
                        {product.name}
                      </h2>

                      <p
                        className={`text-[10px] font-black uppercase tracking-widest ${supportsReservation ? "font-bold text-red-700" : "text-slate-500"}`}
                      >
                        * {toReservationModeLabel(product.productOrderMode)}
                      </p>

                      {product.hasPreparationTime && (product.preparationTimeMinutes ?? 0) > 0 && (
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                          ⏱️ {t("publicMenu:add_meal.preparation_time", { minutes: product.preparationTimeMinutes })}
                        </p>
                      )}
                    </div>

                    <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                      <div className="flex flex-col items-end">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                          {t("publicMenu:add_meal.base_price")}
                        </span>

                        <div
                          className="bg-gray-100 px-4 py-2 rounded-md ring-2 ring-orange-200 shadow-lg flex flex-col items-end"
                          style={
                            isLegacyTheme || !theme
                              ? undefined
                              : {
                                  backgroundColor: theme.secondaryColor,
                                  boxShadow: `0 0 0 2px ${theme.borderColor}`,
                                }
                          }
                        >
                          {hasPromotion && (
                            <span className="text-[9px] font-bold text-gray-500 line-through leading-none mb-0.5">
                              R$ {product.oldPrice?.toFixed(2)}
                            </span>
                          )}

                          {isRodizioIncluded ? (
                            <span className="text-sm font-black uppercase tracking-wide text-emerald-700">
                              {t("rodizio:public.included")}
                            </span>
                          ) : (
                            <span
                              className={`text-lg  font-black leading-none ${hasPromotion ? "text-red-500" : "text-orange-500"}`}
                            >
                              R$ {product.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </Animate>
                  </div>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                  <p
                    className="text-gray-500 text-xs text-justify items-center font-medium leading-relaxed max-w-sm"
                    style={isLegacyTheme || !theme ? undefined : { color: theme.textColor, opacity: 0.75 }}
                  >
                    {product.description}
                  </p>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.7s">
                  {(product.comboItemsPreview?.length ?? 0) > 0 && (
                    <div className="mt-3 rounded-lg border-2 border-orange-200 p-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.15em] text-orange-700">
                        {t("publicMenu:add_meal.combo_items")}
                      </p>

                      <div className="mt-2 space-y-1">
                        {product.comboItemsPreview!.map((comboItem) => (
                          <p key={`${product.id}-${comboItem}`} className="text-[11px] font-semibold text-slate-700">
                            {comboItem}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </Animate>

                {supportsReservation && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
                    <div className="rounded-xl border-2 border-orange-200 p-4 shadow-sm">
                      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em] text-orange-700">
                        <CalendarClock size={14} />
                        {t("publicMenu:add_meal.order_flow_title")}
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {supportsImmediate && (
                          <button
                            type="button"
                            disabled={isReadOnly}
                            onClick={() => setSelectedFlow("immediate")}
                            className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-black uppercase tracking-[0.1em] transition ${
                              selectedFlow === "immediate"
                                ? "border-orange-600 bg-orange-600 text-white"
                                : "border-orange-200 bg-white text-slate-700"
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                            data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.setSelectedFlowImmediate"
                          >
                            <Bolt size={14} />
                            {t("publicMenu:add_meal.order_flow_immediate")}
                          </button>
                        )}

                        {supportsReservation && (
                          <button
                            type="button"
                            disabled={isReadOnly}
                            onClick={() => setSelectedFlow("reservation")}
                            className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-black uppercase tracking-[0.1em] transition ${
                              selectedFlow === "reservation"
                                ? "border-orange-600 bg-orange-600 text-white"
                                : "border-orange-200 bg-white text-slate-700"
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                            data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.setSelectedFlowReservation"
                          >
                            <CalendarClock size={14} />
                            {t("publicMenu:add_meal.order_flow_reservation")}
                          </button>
                        )}
                      </div>

                      {selectedFlow === "reservation" && supportsReservation && (
                        <div className="mt-2 grid grid-cols-1 justify-center items-end gap-2 sm:grid-cols-2">
                          <label className="space-y-1">
                            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                              {t("publicMenu:add_meal.reservation_date")}
                            </span>

                            <select
                              value={reservationDate}
                              disabled={isReadOnly}
                              onChange={(event) => setReservationDate(event.target.value)}
                              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800 ring-2 ring-gray-200 outline-none transition focus:border-orange-400 focus:ring-orange-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                              data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.select.select"
                            >
                              {reservationDates.map((date) => {
                                const value = date.toInputDate()

                                return (
                                  <option key={value} value={value}>
                                    {date.toLocaleDateString()}
                                  </option>
                                )
                              })}
                            </select>
                          </label>

                          <label className="space-y-1">
                            <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                              {t("publicMenu:add_meal.reservation_time")}
                            </span>

                            <select
                              value={reservationTime}
                              disabled={isReadOnly}
                              onChange={(event) => setReservationTime(event.target.value)}
                              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800 ring-2 ring-gray-200 outline-none transition focus:border-orange-400 focus:ring-orange-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                              data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.select.select2"
                            >
                              {(product.reservationAllowedTimes ?? []).map((time) => {
                                const normalizedTime = time.toShortTime()

                                return (
                                  <option key={time} value={normalizedTime}>
                                    {normalizedTime}
                                  </option>
                                )
                              })}
                            </select>
                          </label>
                        </div>
                      )}
                    </div>
                  </Animate>
                )}

                {product.availableAddOns && product.availableAddOns.length > 0 && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <div className="space-y-6">
                      <div className="flex flex-col md:flex-row items-center justify-between">
                        <h3 className="text-sm font-black uppercase tracking-[0.18em] text-gray-600">
                          {t("publicMenu:add_meal.available_addons")}
                        </h3>

                        <span className="text-xs font-semibold text-gray-500 mt-1">
                          {t("publicMenu:add_meal.selected_addons", { count: selectedAddOns.length })} •{" "}
                          {Math.max(
                            0,
                            freeLimit -
                              selectedAddOns.filter((a) => a.isFreeEligible).reduce((sum, a) => sum + a.quantity, 0),
                          )}{" "}
                          {t("publicMenu:add_meal.free_remaining")}
                        </span>
                      </div>

                      <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
                        <div className="space-y-2">
                          {product.availableAddOns.map((addOn) => {
                            const selected = selectedAddOns.find((a) => a.id === addOn.id)

                            const hasAddOnImage = typeof addOn.image === "string" && addOn.image.trim().length > 0

                            const selectedQuantity = selected?.quantity ?? 0

                            const addOnIndex = selectedAddOns.findIndex((item) => item.id === addOn.id)

                            const freeUsedBefore = selectedAddOns
                              .slice(0, Math.max(0, addOnIndex))
                              .filter((item) => item.isFreeEligible)
                              .reduce((sum, item) => sum + item.quantity, 0)

                            const freeQuantity =
                              addOn.isFreeEligible && addOnIndex >= 0
                                ? Math.min(selectedQuantity, Math.max(0, freeLimit - freeUsedBefore))
                                : 0
                            const chargedQuantity = Math.max(0, selectedQuantity - freeQuantity)

                            const addOnTotal = addOn.price * chargedQuantity

                            const hasSelectedQuantity = selectedQuantity > 0

                            const priceLabel = hasSelectedQuantity
                              ? addOnTotal <= 0
                                ? t("publicMenu:add_meal.free_option")
                                : `R$ ${addOn.price.toFixed(2)} x ${chargedQuantity} = R$ ${addOnTotal.toFixed(2)}`
                              : `+ R$ ${addOn.price.toFixed(2)}`

                            const reachedStockLimit =
                              Boolean(addOn.hasStockControl) && selectedQuantity >= (addOn.stockQuantity ?? 0)

                            return (
                              <div
                                key={addOn.id}
                                className={`flex rounded-lg border-2 border-gray-100 bg-gray-50 p-3 ${
                                  hasAddOnImage ? "items-start gap-3" : "flex-col gap-3"
                                }`}
                              >
                                {hasAddOnImage && (
                                  <PublicMenuImageThumbnail
                                    image={addOn.image}
                                    alt={addOn.name}
                                    imageClassName="h-16 w-16 rounded-lg bg-white object-cover ring-1 ring-gray-200 sm:h-20 sm:w-20"
                                    onPreview={(image, alt) => setExpandedImage({ image, alt })}
                                  />
                                )}

                                <div className="flex min-w-0 flex-1 flex-col gap-3">
                                  <div className="min-w-0 text-left text-sm font-black uppercase leading-snug text-gray-800 break-words">
                                    {addOn.name}{" "}
                                    
                                    {addOn.isFreeEligible ? t("publicMenu:add_meal.free_within_limit") : ""}
                                   
                                    {addOn.isOutOfStock && (
                                      <span className="ml-1 inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                        {t("publicMenu:add_meal.addon_out_of_stock")}
                                      </span>
                                    )}
                                  </div>

                                  <div
                                    className={`flex w-full gap-3 ${
                                      hasAddOnImage
                                        ? "flex-col items-end sm:flex-row sm:items-center sm:justify-between"
                                        : "flex-row items-center justify-between"
                                    }`}
                                  >
                                    <div className="min-w-0 text-sm font-black leading-snug text-green-600 break-words">
                                      {priceLabel}
                                    </div>

                                    {!isReadOnly && (
                                      <div className="flex justify-end w-fit shrink-0 items-center gap-2 rounded-lg border-2 border-gray-100 bg-white p-1">
                                        <button
                                          type="button"
                                          disabled={!selected}
                                          onClick={() => decreaseAddOn(addOn.id)}
                                          className="flex h-9 w-9 items-center justify-center rounded-md bg-gray-200 text-lg font-black text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.decreaseAddOnAddOnId"
                                        >
                                          −
                                        </button>

                                        <span className="w-8 text-center text-base font-black text-gray-900 tabular-nums">
                                          {selectedQuantity}
                                        </span>

                                        <button
                                          type="button"
                                          disabled={reachedStockLimit}
                                          onClick={() => increaseAddOn(addOn)}
                                          className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-600 text-lg font-black text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-40"
                                          data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.increaseAddOnAddOn"
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </Animate>
                    </div>
                  </Animate>
                )}

                {product.availableSelections && product.availableSelections.length > 0 && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                    <div className="space-y-3">
                      <h3 className="text-[12px] font-black uppercase tracking-[0.25em] text-gray-500">
                        {t("publicMenu:add_meal.selections")}
                      </h3>
                      {product.availableSelections.map((selection) => {
                        const selectedCount = selectedSelections.filter(
                          (item) => item.selectionId === selection.id,
                        ).length

                        const requiredOptionsCount = Math.max(1, selection.requiredOptionsCount ?? 1)

                        const isMissingRequiredSelection = selection.isRequired && selectedCount < requiredOptionsCount

                        return (
                          <div
                            key={selection.id}
                            className={`rounded-lg border-2 p-4 transition-colors ${
                              isMissingRequiredSelection
                                ? "border-red-300 bg-red-50/80 shadow-sm shadow-red-100"
                                : "border-gray-200 bg-white"
                            }`}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <p className="text-sm font-black text-slate-800 uppercase leading-snug">
                                {selection.name}
                              </p>

                              {isMissingRequiredSelection && (
                                <span className="rounded-md bg-red-600 px-2 py-1 text-[11px] font-black uppercase text-white">
                                  {t("publicMenu:add_meal.pending_selection")}
                                </span>
                              )}
                            </div>

                            <div className="mt-1 flex flex-wrap gap-1">
                              {buildSelectionStatusMessages(selection, selectedSelections, t).map((status) => (
                                <span
                                  key={`${selection.id}-${status.text}`}
                                  className={`inline-flex items-center rounded-md border px-2 py-1 text-[11px] font-black ${status.className}`}
                                >
                                  {status.text}
                                </span>
                              ))}
                            </div>

                            {isMissingRequiredSelection && (
                              <p className="mt-2 text-sm font-bold leading-snug text-red-700">
                                {t("publicMenu:add_meal.required_selection_notice", {
                                  selection: selection.name,
                                  count: requiredOptionsCount - selectedCount,
                                })}
                              </p>
                            )}

                            <div className="mt-3 space-y-2">
                              {selection.options.map((option) => {
                                const hasOptionImage =
                                  typeof option.image === "string" && option.image.trim().length > 0

                                const optionSelectedCount = selectedSelections.filter(
                                  (selected) =>
                                    selected.selectionId === selection.id && selected.optionId === option.id,
                                ).length

                                const checked = optionSelectedCount > 0

                                const currentSelectedCount = selectedSelections.filter(
                                  (selected) => selected.selectionId === selection.id,
                                ).length

                                const effectiveMaximumSelectionsLimit = getEffectiveSelectionMaximum(selection)

                                const optionAllowsQuantity = selection.allowMultiple && option.allowMultiple

                                const optionMaximum = optionAllowsQuantity
                                  ? Math.max(1, option.maximumSelectionsLimit ?? 1)
                                  : 1

                                const reachedSelectionMaximum =
                                  effectiveMaximumSelectionsLimit !== undefined &&
                                  currentSelectedCount >= effectiveMaximumSelectionsLimit

                                const reachedOptionMaximum = optionSelectedCount >= optionMaximum

                                const reachedMaximum = !checked && (reachedSelectionMaximum || reachedOptionMaximum)

                                const baseValue = option.isFree ? 0 : option.value

                                const selectedWithinSelection = selectedSelections.filter(
                                  (selected) => selected.selectionId === selection.id,
                                )

                                const previewItems = checked
                                  ? selectedWithinSelection
                                  : [...selectedWithinSelection, createSelectionItem(selection, option)]

                                const previewValues = resolveSelectionValues(selection, previewItems).filter(
                                  (item) => item.optionId === option.id,
                                )

                                const previewValue = previewValues[previewValues.length - 1]?.value ?? baseValue

                                if (optionAllowsQuantity) {
                                  const reachedStockLimit =
                                    Boolean(option.hasStockControl) &&
                                    optionSelectedCount >= (option.stockQuantity ?? 0)

                                  const canIncrease =
                                    !isReadOnly &&
                                    !reachedSelectionMaximum &&
                                    !reachedOptionMaximum &&
                                    !reachedStockLimit

                                  const selectedOptionValues = selectedSelections.filter(
                                    (item) => item.selectionId === selection.id && item.optionId === option.id,
                                  )

                                  const selectedOptionTotal = selectedOptionValues.reduce(
                                    (sum, item) => sum + Number(item.value ?? 0),
                                    0,
                                  )

                                  const chargedOptionCount = selectedOptionValues.filter(
                                    (item) => Number(item.value ?? 0) > 0,
                                  ).length

                                  const hasSelectedQuantity = optionSelectedCount > 0

                                  const unitValue = baseValue

                                  const displayValue = hasSelectedQuantity ? selectedOptionTotal : previewValue

                                  const chargedPriceDisplay =
                                    hasSelectedQuantity && displayValue > 0
                                      ? {
                                          label: `R$ ${unitValue.toFixed(2)} x ${chargedOptionCount} = R$ ${displayValue.toFixed(2)}`,
                                          isFreeStyle: false,
                                        }
                                      : ResolveSelectionOptionPriceLabel(
                                          hasSelectedQuantity ? displayValue : previewValue,
                                          product.price,
                                          companyMemory.zeroValueSelectionOptionDisplayMode,
                                        )

                                  return (
                                    <div
                                      key={option.id}
                                      className={`flex min-h-12 flex-col items-stretch justify-between gap-2 rounded-lg border px-3 py-2 text-sm transition sm:flex-row sm:items-center sm:gap-3 ${
                                        checked
                                          ? "border-orange-300 bg-orange-50 ring-1 ring-orange-200"
                                          : isReadOnly
                                            ? "border-gray-200 bg-gray-50 opacity-60"
                                            : "border-gray-200 bg-white hover:border-orange-200 hover:bg-orange-50/40"
                                      }`}
                                    >
                                      <span className="flex w-full min-w-0 items-center gap-3 font-bold leading-snug text-slate-800 break-words sm:w-auto sm:flex-1">
                                        {hasOptionImage && (
                                          <PublicMenuImageThumbnail
                                            image={option.image}
                                            alt={option.name}
                                            imageClassName="h-14 w-14 rounded-lg bg-white object-cover ring-1 ring-gray-200"
                                            onPreview={(image, alt) => setExpandedImage({ image, alt })}
                                          />
                                        )}

                                        <span className="min-w-0">{option.name}</span>

                                        {option.isOutOfStock && (
                                          <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                            {t("publicMenu:add_meal.option_out_of_stock")}
                                          </span>
                                        )}
                                      </span>

                                      <div className="flex w-full shrink-0 items-center justify-between gap-2 sm:w-auto sm:justify-end">
                                        {chargedPriceDisplay.label && (
                                          <span
                                            className={`min-w-0 flex-1 rounded-md px-2 py-1 text-xs font-black sm:flex-none ${
                                              chargedPriceDisplay.isFreeStyle
                                                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                                : "bg-orange-50 text-orange-700 ring-1 ring-orange-200"
                                            }`}
                                          >
                                            {chargedPriceDisplay.label}
                                          </span>
                                        )}

                                        {!isReadOnly && (
                                          <div className="flex items-center gap-1 rounded-lg border border-gray-100 bg-white p-1">
                                            <button
                                              type="button"
                                              disabled={optionSelectedCount <= 0}
                                              onClick={() => decreaseSelectionOption(selection, option)}
                                              className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-200 text-base font-black text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                                              data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.decreaseSelectionOptionSelectionOption"
                                            >
                                              -
                                            </button>

                                            <span className="w-7 text-center text-sm font-black tabular-nums text-gray-900">
                                              {optionSelectedCount}
                                            </span>

                                            <button
                                              type="button"
                                              disabled={!canIncrease}
                                              onClick={() => increaseSelectionOption(selection, option)}
                                              className="flex h-8 w-8 items-center justify-center rounded-md bg-orange-600 text-base font-black text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
                                              data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.increaseSelectionOptionSelectionOption"
                                            >
                                              +
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                }

                                const singlePriceDisplay = ResolveSelectionOptionPriceLabel(
                                  previewValue,
                                  product.price,
                                  companyMemory.zeroValueSelectionOptionDisplayMode,
                                )

                                return (
                                  <label
                                    key={option.id}
                                    className={`flex min-h-12 items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm transition ${
                                      checked
                                        ? "border-orange-300 bg-orange-50 ring-1 ring-orange-200"
                                        : reachedMaximum || isReadOnly || option.isOutOfStock
                                          ? "border-gray-200 bg-gray-50 opacity-60"
                                          : "border-gray-200 bg-white hover:border-orange-200 hover:bg-orange-50/40"
                                    }`}
                                  >
                                    <span className="flex min-w-0 items-center gap-3 font-bold text-slate-800">
                                      <input
                                        type={selection.allowMultiple ? "checkbox" : "radio"}
                                        name={`selection-${selection.id}`}
                                        checked={checked}
                                        disabled={isReadOnly || reachedMaximum || option.isOutOfStock}
                                        onChange={() => toggleSelectionOption(selection, option, checked)}
                                        className="sr-only"
                                        data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.input.selection"
                                      />{" "}
                                      {hasOptionImage && (
                                        <PublicMenuImageThumbnail
                                          image={option.image}
                                          alt={option.name}
                                          imageClassName="h-14 w-14 rounded-lg bg-white object-cover ring-1 ring-gray-200"
                                          onPreview={(image, alt) => setExpandedImage({ image, alt })}
                                        />
                                      )}
                                      <span
                                        aria-hidden="true"
                                        className={`flex h-6 w-6 shrink-0 items-center justify-center border-2 ${
                                          selection.allowMultiple ? "rounded-md" : "rounded-full"
                                        } ${
                                          checked
                                            ? "border-orange-600 bg-orange-600 text-white"
                                            : "border-slate-300 bg-white"
                                        }`}
                                      >
                                        {checked && (
                                          <svg
                                            className="h-3.5 w-3.5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="4"
                                              d="m5 13 4 4L19 7"
                                            />
                                          </svg>
                                        )}
                                      </span>
                                      <span className="min-w-0 leading-snug break-words">{option.name}</span>

                                      {option.isOutOfStock && (
                                        <span className="inline-flex shrink-0 items-center rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                          {t("publicMenu:add_meal.option_out_of_stock")}
                                        </span>
                                      )}
                                    </span>

                                    {singlePriceDisplay.label && (
                                      <span
                                        className={`shrink-0 rounded-md px-2 py-1 text-xs font-black ${
                                          singlePriceDisplay.isFreeStyle
                                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                            : "bg-orange-50 text-orange-700 ring-1 ring-orange-200"
                                        }`}
                                      >
                                        {singlePriceDisplay.label}
                                      </span>
                                    )}
                                  </label>
                                )
                              })}
                            </div>

                            {selection.isRequired && (
                              <p className="mt-2 text-xs font-bold text-red-600">
                                * {t("publicMenu:add_meal.required_selection")}
                              </p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </Animate>
                )}

                {!isReadOnly && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
                    <div className="space-y-4 pt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <div className="space-y-4">
                          <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400">
                            {t("publicMenu:add_meal.quantity")}
                          </h3>

                          <div className="flex items-center justify-between ring-2 ring-slate-100 p-2 rounded-2xl shadow-xl border border-white/10">
                            <button
                              onClick={() => setQuantity(Math.max(1, quantity - 1))}
                              className="w-12 h-12 ring-2 ring-orange-500 hover:bg-orange-200 text-orange-500 rounded-xl flex items-center justify-center transition-all active:scale-90"
                              data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.setQuantityMathMax1Quantity1"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M20 12H4" />
                              </svg>
                            </button>

                            <span
                              className="text-2xl font-black tabular-nums"
                              style={{ color: theme?.textColor || "#111827" }}
                            >
                              {quantity}
                            </span>

                            <button
                              onClick={() => setQuantity(quantity + 1)}
                              className="w-12 h-12 ring-2 ring-orange-500 hover:bg-orange-200 text-orange-500 rounded-xl flex items-center justify-center transition-all active:scale-90"
                              data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.setQuantityQuantity1"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h3 className="text-[10px] font-black uppercase tracking-[0.25em]  text-gray-400">
                            {t("publicMenu:add_meal.notes")}
                          </h3>

                          <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t("publicMenu:add_meal.notes_placeholder")}
                            className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white outline-none transition-all font-medium text-gray-800 text-xs h-[64px] resize-none placeholder-gray-300 shadow-inner"
                            data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.input.notesPlaceholder"
                          />
                        </div>
                      </div>
                    </div>
                  </Animate>
                )}
              </div>

              <div
                className="px-4 py-4 sm:px-8 sm:py-6 md:px-10 md:py-8 bg-white border-t flex flex-col gap-3 shrink-0"
                style={
                  isLegacyTheme || !theme
                    ? undefined
                    : { backgroundColor: theme.surfaceColor, borderColor: theme.borderColor }
                }
              >
                {hasMissingRequiredSelections && (
                  <div className="w-full rounded-lg border-2 border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-800">
                    {t("publicMenu:add_meal.missing_required_summary", {
                      selections: missingRequiredSelectionNames,
                    })}
                  </div>
                )}

                <div className="flex w-full items-center justify-between gap-3 sm:gap-6">
                  <Animate type={AnimationTypes.slideRightIn}>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] mb-1">
                        {t("publicMenu:add_meal.total")}
                      </span>

                      {isRodizioIncluded && currentTotalPrice <= 0 ? (
                        <span className="text-lg font-black uppercase tracking-wide text-emerald-700">
                          {t("rodizio:public.included")}
                        </span>
                      ) : (
                        <div className="flex items-baseline gap-1">
                          <span className={`text-xs font-black ${hasPromotion ? "text-red-600" : "text-orange-600"}`}>
                            R$
                          </span>

                          <span
                            className={`text-2xl sm:text-4xl font-black tracking-tighter ${hasPromotion ? "text-red-600" : ""}`}
                            style={hasPromotion ? undefined : { color: theme?.textColor || "#111827" }}
                          >
                            {Math.floor(currentTotalPrice)}
                            <span className="text-xl opacity-30">
                              ,{(currentTotalPrice % 1).toFixed(2).substring(2)}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </Animate>

                  {!hideConfirm && (
                    <Animate type={AnimationTypes.slideRightIn}>
                      <button
                        onClick={() => {
                          onConfirm(product, selectedAddOns, selectedSelections, quantity, notes, {
                            isReservation: selectedFlow === "reservation",
                            reservationDate: selectedFlow === "reservation" ? reservationDate : "",
                            reservationTime: selectedFlow === "reservation" ? reservationTime : "",
                          })
                        }}
                        disabled={disableConfirm || hasMissingRequiredSelections}
                        className={`flex-1 sm:flex-none group/btn relative ${
                          hasMissingRequiredSelections
                            ? "bg-gray-300 text-gray-600 shadow-none ring-2 ring-gray-200"
                            : isReadOnly
                              ? "bg-red-600 ring-1 ring-black text-white"
                              : selectedFlow === "reservation"
                                ? "bg-orange-500 hover:bg-orange-600 text-white"
                                : hasPromotion
                                  ? "bg-red-600 hover:bg-red-700 text-white"
                                  : "bg-red-600 hover:bg-orange-600 text-white"
                        } px-4 sm:px-5 h-12 sm:h-16 rounded-lg transition-all duration-300 shadow-2xl active:scale-95 flex items-center justify-center gap-2 sm:gap-4 overflow-hidden disabled:cursor-not-allowed disabled:active:scale-100`}
                        style={
                          hasMissingRequiredSelections || isLegacyTheme || !theme
                            ? undefined
                            : isReadOnly
                              ? { backgroundColor: askWaiterButtonColor, color: askWaiterButtonTextColor }
                              : { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
                        }
                        data-interaction-description-key="interactionDescriptions:publicMenu.pupop.addMealRequestPupop.button.completeRequiredSelections"
                      >
                        <span className="relative z-10 font-black text-xs uppercase tracking-[0.2em]">
                          {hasMissingRequiredSelections
                            ? t("publicMenu:add_meal.complete_required_selections")
                            : (confirmLabel ??
                              (selectedFlow === "reservation"
                                ? t("publicMenu:add_meal.reserve_item")
                                : t("publicMenu:add_meal.add_item")))}
                        </span>

                        <div
                          className={`relative z-10 w-7 h-7 rounded-xl flex items-center justify-center transition-all ${
                            isReadOnly ? "bg-black/5" : "bg-white/10 group-hover/btn:bg-white/20"
                          }`}
                        >
                          <svg
                            className="w-4 h-4 group-hover/btn:rotate-90 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M12 4v16m8-8H4" />
                          </svg>
                        </div>

                        <div className="absolute top-0 -left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover/btn:left-[100%] transition-all duration-1000" />
                      </button>
                    </Animate>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Animate>
      </div>

      {expandedImage && (
        <PublicMenuImagePreviewPupop
          image={expandedImage.image}
          title={expandedImage.alt}
          theme={theme}
          onClose={() => setExpandedImage(null)}
        />
      )}

      {expandedProductImageIndex !== null && (
        <PublicMenuImagePreviewPupop
          images={productImages}
          initialIndex={expandedProductImageIndex}
          title={product.name}
          theme={theme}
          onClose={() => setExpandedProductImageIndex(null)}
        />
      )}
    </ModalPortal>
  )
}
